import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useFieldArray, useForm } from "react-hook-form";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import {
  QualificationQuestion,
  useQualificationQuestions,
} from "lib/hooks/api/bid/bid_question/useQualificationQuestions";
import { useSaveQualificationQuestions } from "lib/hooks/api/bid/bid_question/useSaveQualificationQuestions";
import { LoadingState } from "../../app_layout/LoadingState";
import toast from "../../ui/toast/Toast";
import { DeleteCriteriaModal } from "../qualification-criteria/DeleteCriteriaModal";
import { Content, Page } from "../workflow_stages/shared";
import AddOrEditQuestionDrawer from "./AddOrEditQuestionDrawer";
import QuestionRow from "./QuestionRow";

type QuestionFormField = {
  /** Undefined for newly created questions (not yet persisted). */
  id?: string;
  title: string;
  question: string;
  order: number;
  /** RHF-generated key for stable React keys. */
  key: string;
};

type QuestionFormValues = {
  questions: QuestionFormField[];
};

type DrawerState = { mode: "add" } | { mode: "edit"; index: number; initialQuestionText: string };

export function questionToFormField(q: QualificationQuestion): Omit<QuestionFormField, "key"> {
  return {
    id: q.id,
    title: q.title,
    question: q.question,
    order: q.order ?? 0,
  };
}

function formFieldsToSavePayload(fields: QuestionFormField[]) {
  return fields.map((f, i) => ({
    ...(f.id ? { id: f.id } : {}),
    title: f.title,
    question: f.question,
    order: i + 1,
  }));
}

export default function EditQualificationReportQuestions(): JSX.Element {
  const { data, isLoading, isError } = useQualificationQuestions();
  const { mutate: saveQuestions } = useSaveQualificationQuestions({
    onSuccess: () => toast.success("Qualification question updated"),
    onError: () => toast.error("Unable to update your question; please try again"),
  });

  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ index: number; title: string } | null>(null);

  const { watch, reset, control } = useForm<QuestionFormValues>({
    defaultValues: { questions: [] },
  });
  const { replace, move } = useFieldArray({ control, name: "questions", keyName: "key" });

  // Sync form with server data
  useEffect(() => {
    if (!data) return;
    reset({
      questions: [...data.questions]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(questionToFormField),
    });
  }, [data, reset]);

  const currentQuestions = watch("questions");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const save = useCallback(
    (fields: QuestionFormField[]) => {
      saveQuestions({
        req: { questions: formFieldsToSavePayload(fields) },
      });
    },
    [saveQuestions],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;

      const activeIdx = currentQuestions.findIndex((q) => (q.id ?? q.key) === active.id);
      const overIdx = currentQuestions.findIndex((q) => (q.id ?? q.key) === over.id);
      if (activeIdx === -1 || overIdx === -1) return;

      flushSync(() => move(activeIdx, overIdx));

      const reordered = arrayMove([...currentQuestions], activeIdx, overIdx).map((q, i) => ({
        ...q,
        order: i + 1,
      }));
      replace(reordered);
      save(reordered);
    },
    [currentQuestions, move, replace, save],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newQuestions = currentQuestions
        .filter((_, i) => i !== index)
        .map((q, i) => ({ ...q, order: i + 1 }));
      replace(newQuestions);
      save(newQuestions);
    },
    [currentQuestions, replace, save],
  );

  const handleDrawerSave = useCallback(
    (questionText: string) => {
      if (!drawerState) return;

      if (drawerState.mode === "edit") {
        const newQuestions = currentQuestions.map((q, i) =>
          i !== drawerState.index ? q : { ...q, title: questionText, question: questionText },
        );
        replace(newQuestions);
        save(newQuestions);
      } else {
        const newQuestion: Omit<QuestionFormField, "key"> = {
          title: questionText,
          question: questionText,
          order: currentQuestions.length + 1,
        };
        const newQuestions = [...currentQuestions, newQuestion as QuestionFormField];
        replace(newQuestions);
        save(newQuestions);
      }

      setDrawerState(null);
    },
    [drawerState, currentQuestions, replace, save],
  );

  if (isLoading) {
    return (
      <Page>
        <Content>
          <LoadingState title="Loading qualification questions" description="" />
        </Content>
      </Page>
    );
  }

  if (isError) {
    return (
      <Page>
        <Content>
          <EmptyState
            heading="Sorry, something has gone wrong"
            description="If the error persists, please contact support"
            type="loadError"
            size="large"
          />
        </Content>
      </Page>
    );
  }

  return (
    <Page>
      <Content>
        <Flex column gap={48} width="100%" alignItems="stretch">
          <Flex gap={16} alignItems="center" alignSelf="stretch">
            <Flex gap={8} column>
              <Text h1>Qualification criteria</Text>
              <Text>
                Set the criteria that determine whether an opportunity is worth bidding on. The AI
                will evaluate each one against the tender documents and your content library.
              </Text>
            </Flex>
            <Button
              buttonType="secondary"
              icon="plus"
              onClick={() => setDrawerState({ mode: "add" })}
            >
              Question
            </Button>
          </Flex>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={currentQuestions.map((q) => q.id ?? q.key)}
              strategy={verticalListSortingStrategy}
            >
              <QuestionsContainer aria-label="Qualification questions">
                {currentQuestions.map((q, index) => (
                  <QuestionRow
                    key={q.key}
                    id={q.id ?? q.key}
                    questionText={q.question}
                    onStartEdit={() =>
                      setDrawerState({
                        mode: "edit",
                        index,
                        initialQuestionText: q.question,
                      })
                    }
                    onDelete={() => setDeleteTarget({ index, title: q.question })}
                  />
                ))}
              </QuestionsContainer>
            </SortableContext>
          </DndContext>
        </Flex>

        <AddOrEditQuestionDrawer
          isOpen={drawerState !== null}
          onClose={() => setDrawerState(null)}
          initialQuestionText={
            drawerState?.mode === "edit" ? drawerState.initialQuestionText : undefined
          }
          onSave={handleDrawerSave}
        />

        {deleteTarget && (
          <DeleteCriteriaModal
            open={true}
            onOpenChange={(open) => {
              if (!open) setDeleteTarget(null);
            }}
            criteriaTitle={deleteTarget.title}
            deleteQuestion={() => {
              handleDelete(deleteTarget.index);
              setDeleteTarget(null);
            }}
          />
        )}
      </Content>
    </Page>
  );
}

const QuestionsContainer = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
}));
